import { Component, Input, OnInit, OnDestroy, Inject, ElementRef, ChangeDetectorRef, Renderer2, ViewEncapsulation } from '@angular/core';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { filter } from 'rxjs/operators';
import { LaneManagerService } from 'shared/components/lane-buttons/shared/services/lane-manager/lane-manager.service';
import { WorkItemLaneType } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane-type.enum';
import { IntegrationWorkItemLaneModel, WorkItemLaneModel } from 'shared/components/lane-buttons/shared/services/lane-manager/work-item-lane.model';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { WorkAreaManager } from 'common/workitems/work-area-manager.service';
import { CannedMessageRequest } from 'common/canned-messages/canned-message-request.interface';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Subscription } from 'rxjs';
import { WorkItemsTrackingService } from 'common/workitems/workitems-tracking.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { WidgetBase } from 'common/panel-layout';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { NanorepService } from 'shared/components/nanorep-panel/nanorep.service';
import { TextChatItem } from 'common/workitems/interfaces/text-chat.interface';
import { CannedMessagePanelComponent } from 'shared/components/canned-message-panel/canned-message-panel.component';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';
import { WorkItemObservableCacheService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/work-item-observable-cache.service';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { BcAuth } from 'common/auth/auth.service';
import { BcConfigService } from 'common/config/provider';
import { UserPermissionService } from 'shared/services/user-permissions/user-permission.service';
import { AccountPermissionType } from 'common/common/constants/account-permission-type';

const SCROLL_SMOOTH_ANIMATION_LENGTH = 300;

@Component({
  selector: 'app-text-chat-widget',
  templateUrl: './text-chat-widget.component.html',
  styleUrls: ['./text-chat-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TextChatWidgetComponent extends WidgetBase implements OnInit, OnDestroy {

  @Input() public workItem: TextChatItem;

  public isMultiLane: boolean;

  public operator;
  public cannedMessageOpenSubscription: Subscription;
  public cannedMessageCloseSubscription: Subscription;
  public cannedMessageSelectionSubscription: Subscription;
  public cannedMessageRequest: CannedMessageRequest;
  public customTabOpenSubscription: Subscription;
  public textChatSubscription: Subscription;
  public textChat;
  public textChatThemeClass: string;
  public visitor: VisitorInfoModel;
  public laneSubscription;
  public laneToggledSubscription;
  public workItemLanes: WorkItemLaneModel[];
  public integrationWorkItemLanes;
  public expandedModeTime: number;
  readonly instantlyExpandedDelay = 100; // ms
  public trackInit = false;
  public isSmartAdvisor: boolean = false;
  public hasAssignmentHistoryPermission: boolean = false;

  private _isNanorepEnabled: boolean = false;
  private _isFeatureConfigLoaded: boolean = false;
  private _toggleExpandedSubscription: Subscription;
  private _trackSelectedItemSubscription: Subscription;
  private _waitForTrack = false;
  private _visitorObservable: Subscription;

  constructor(
    private bcAuth: BcAuth,
    bcConfig: BcConfigService,
    private bcAnalytics: BcAnalytics,
    private visitorInfoBuilderService: BcVisitorInfoBuilderService,
    private _workAreaManager: WorkAreaManager,
    private laneManager: LaneManagerService,
    @Inject('velocityjs') private velocityjs,
    private nanorepService: NanorepService,
    private _rxEmitter: RxEmitter,
    private bcWorkItemsTrackingService: WorkItemsTrackingService,
    private _element: ElementRef,
    renderer: Renderer2,
    private _changeDetector: ChangeDetectorRef,
    private _workItemObservableCache: WorkItemObservableCacheService,
    private _userPermissionService: UserPermissionService
  ) {
    super(_element.nativeElement, _workAreaManager, renderer, _rxEmitter);

    this.isMultiLane = bcConfig.current.features.multiLane;

    this.operator = this.bcAuth.getSession();

    this.cannedMessageOpenSubscription = null;
    this.cannedMessageCloseSubscription = null;
    this.cannedMessageSelectionSubscription = null;
  }

  ngOnInit() {
    this._initTrackEvents();

    this.loadFeatureValues();

    this.initLaneObservable();

    this.initObservable();

    this.initMessageBus();

    this.initLaneToggledObservable();

    this._checkPermissionForAssignmentHistory();
  }

  ngOnDestroy() {
    this._safeUnsubscribe(this.cannedMessageOpenSubscription);
    this._safeUnsubscribe(this.cannedMessageCloseSubscription);
    this._safeUnsubscribe(this.cannedMessageSelectionSubscription);
    this._safeUnsubscribe(this.cannedMessageFolderDropdownSubscription);

    this._safeUnsubscribe(this._toggleExpandedSubscription);
    this._safeUnsubscribe(this._trackSelectedItemSubscription);

    this._safeUnsubscribe(this.laneSubscription);
    this._safeUnsubscribe(this.laneToggledSubscription);

    this.unregisterPanelCloseListeners();
    this.bcWorkItemsTrackingService.destroyItem(this.workItem.id);

    this._safeUnsubscribe(this._visitorObservable);
  }

  private _safeUnsubscribe(sub: Subscription): void {
    if (sub) {
      sub.unsubscribe();
      sub = null;
    }
  }

  get isCannedMessagesLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.CannedMessage);
  }

  get isSmartAdvisorLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.SmartAdvisor);
  }

  get isVisitorInfoLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.VisitorInfo);
  }

  get isVisitorHistoryLaneVisible(): boolean {
    return this._isLaneVisible(WorkItemLaneType.VisitorHistory);
  }

  private _checkPermissionForAssignmentHistory(): void {
    this.hasAssignmentHistoryPermission = this._userPermissionService.hasAccountPermissionFor(AccountPermissionType.AssignmentHistory);
  }

  private _isInstantlyExpanded(): boolean {
    if (this.isExpandedMode()) {
      const expandedModeTime = this.expandedModeTime || 0;
      return Date.now() - expandedModeTime < this.instantlyExpandedDelay;
    } else {
      return false;
    }
  }

  private _initTrackEvents(): void {
    this._toggleExpandedSubscription = this._rxEmitter.subscribe(MessageBusEvents.ToggleExpandedMode, () => {
      this.expandedModeTime = Date.now();
    });

    this._trackSelectedItemSubscription = this.bcWorkItemsTrackingService.onChange.pipe(filter(id => id === this.workItem.id)).subscribe(() => {
      if (!this.isExpandedMode() && this.trackInit || !this._isInstantlyExpanded() && this.isExpandedMode() && this.trackInit) {
        this._trackSelectedEvent();
      } else {
        this._waitForTrack = true;
      }
    });
  }

  private _trackSelectedEvent(): void {
    this._waitForTrack = false;
    this.bcWorkItemsTrackingService.track(this.workItem);
  }

  isExpandedMode(): boolean {
    return this._workAreaManager.isExpandedMode() || !this.workItem.isCollapsible;
  }

  _isLaneVisible(laneType: WorkItemLaneType): boolean {
    // Lane visibility togglers used only in expanded mode
    if (!this.isExpandedMode()) {
      return true;
    }

    const lane = this.workItemLanes && this.workItemLanes.find(workItemLane => workItemLane.type === laneType);
    return lane && lane.visible;
  }

  initObservable(): void {
    this.resetState();

    this.initDataForTextChat();
  }

  initMessageBus(): void {
    this.cannedMessageOpenSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ActivateCannedMessagePanel}${this.workItem.id}`, (value) => {
      this.showAndOpenCannedMessagePanel(value && value.isSmartAdvisor);
    });

    this.customTabOpenSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.ActivateCustomTabPanel}-${this.workItem.id}`,
      () => this.showAndOpenCustomTabPanel());

    this.cannedMessageCloseSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, () => {
      this.closeCannedMessagePanel();
    });

    this.cannedMessageSelectionSubscription = this._rxEmitter.subscribe(`${MessageBusEvents.SendCannedMessageToEditor}${this.workItem.id}`, (args) => {
      if (args.eventSource === CannedMessagePanelComponent.name) {
        this.closeCannedMessagePanel();
      }
    });
  }

  initLaneObservable(): void {
    this.laneSubscription = this.laneManager.getLaneObservable(this.workItem.id)
      .subscribe(lanes => {
        this.workItemLanes = lanes;

        if (this._waitForTrack) {
          this._trackSelectedEvent();
        }

        this.integrationWorkItemLanes = this.workItemLanes
          .filter(lane => this.isLaneIntegration(lane));
      });
  }

  initLaneToggledObservable(): void {
    this.laneToggledSubscription = this.laneManager.getLaneToggledObservable(this.workItem.id)
      .subscribe(toggledLane => {
        if (!toggledLane.visible || !this.isLaneIntegration(toggledLane)) {
          return;
        }

        this._changeDetector.detectChanges();
        this._scrollToLane(<IntegrationWorkItemLaneModel> toggledLane);
      });
  }

  isEmbeddedIntegration(workItemLane: WorkItemLaneModel): boolean {
    return workItemLane.type === WorkItemLaneType.EmbeddedIntegration;
  }

  isSalesforceIntegration(workItemLane: WorkItemLaneModel): boolean {
    return workItemLane.type === WorkItemLaneType.SalesforceIntegration;
  }

  isLaneIntegration(workItemLane: WorkItemLaneModel): boolean {
    return this.isEmbeddedIntegration(workItemLane) || this.isSalesforceIntegration(workItemLane);
  }

  resetState(): void {
    this.textChat = null;
    this.visitor = null;
    this.textChatThemeClass = 'theme-1';
  }

  initDataForTextChat(): void {
    const workItemId = this.getActualWorkItemId();

    if (this.textChatSubscription) {
      this.textChatSubscription.unsubscribe();
    }

    if (this._visitorObservable) {
      this._visitorObservable.unsubscribe();
    }

    // SMS WorkItemType is used, as it is the same observable for every TextChat types
    this.textChatSubscription = this._workItemObservableCache.getWorkItemObservable(workItemId, WorkItemTypes.SMS).subscribe(textChat => {
      this.setTextChat(textChat);
      this._changeDetector.detectChanges();

      this.trackInit = true;

      if (this._waitForTrack && !this.isExpandedMode()) {
        this._trackSelectedEvent();
      }
    });

    this._visitorObservable = this.visitorInfoBuilderService.observeVisitorData(workItemId, WorkItemTypes.SMS).subscribe((resp) => {
      this.visitor = resp;
      this._changeDetector.detectChanges();
    });
  }

  setTextChat(textChat): void {
    this.textChat = textChat;
    this.getTextChatClass();

    this.cannedMessageRequest = {
      type: bcEnums.cannedMessageTypes.TEXTCHAT,
      textChatId: this.textChat.TextChatID
    };
  }

  getTextChatClass(): void {
    this.textChatThemeClass = ThemeServiceHelper.getCssClass(this.textChat.TextChatID);
  }

  isTextChatActive(): boolean {
    return this.textChat && this.textChat.isConversationActive(this.operator);
  }

  _sendCannedMessagePanelAnalytics(action): void {
    this.bcAnalytics.eventTrack('TextChatCMPanel', { action });
  }

  private _scrollToLane(lane: IntegrationWorkItemLaneModel): void {
    const multiLaneWrapperElement = this._element.nativeElement.querySelector('.multilane-wrapper');
    const laneElement = multiLaneWrapperElement.querySelector(`[data-integration-id="${lane.id}"]`);

    if (laneElement) {
      this.velocityjs(laneElement, 'scroll', {
        axis: 'x',
        duration: SCROLL_SMOOTH_ANIMATION_LENGTH,
        container: multiLaneWrapperElement
      });
    } else {
      window.requestAnimationFrame(() => {
        this._scrollToLane(lane);
      });
    }
  }

  showAndOpenCannedMessagePanel(isSmartAdvisor: boolean = false): void {
    this.ensureCannedMessagesLaneVisible();
    this.isSmartAdvisor = isSmartAdvisor;
    this.openCannedMessagePanel(false);
  }

  showAndOpenCustomTabPanel(): void {
    this.ensureCannedMessagesLaneVisible();
    this.openCannedMessagePanel(true);
    this._rxEmitter.send(MessageBusEvents.RequestCustomTabPanel, null);
  }

  ensureCannedMessagesLaneVisible(): void {
    if (!this.isCannedMessagesLaneVisible) {
      this.laneManager.toggleLane(this.workItem.id, WorkItemLaneType.CannedMessage.toString(), this.workItem.type);
    }
  }

  onNanorepPanelClose(): void {
    this._rxEmitter.send(`${MessageBusEvents.CloseCannedMessagePanel}${this.workItem.id}`, null);
  }

  doesCustomerUseNanorep(): boolean {
    return this.isNanorepEnabled() && !!this.workItem.NanorepConfigID;
  }

  isNanorepEnabled(): boolean {
    return this._isNanorepEnabled;
  }

  isFeatureConfigLoaded(): boolean {
    return this._isFeatureConfigLoaded;
  }

  async loadFeatureValues(): Promise<void> {
    [this._isNanorepEnabled] = await Promise.all([
      this.nanorepService.hasNanorepFeature()
    ]);

    this.trackInit = true;

    if (this._waitForTrack && !this.isExpandedMode()) {
      this._trackSelectedEvent();
    }

    this._isFeatureConfigLoaded = true;
  }

  reloadIFrameUrlFromServer(integrationLane: IntegrationWorkItemLaneModel): void {
    this.laneManager.updateIntegrationUrl(this.workItem.id, integrationLane, this.workItem.type);
  }

  isVisitorInfoUnchanged(a, b): boolean {  // no visitorinfo type??
    return a.DepartmentID === b.DepartmentID
      && a.FolderID === b.FolderID
      && a.VisitEmailAddress === b.VisitEmailAddress
      && a.CustomField1 === b.CustomField1
      && a.CustomField2 === b.CustomField2;
  }

  getActualWorkItemId(): string {
    return this.workItem.id;
  }

  getRelation(): string {
    return (this.textChat.Email || '') + ':' + (this.textChat.PhoneNumber || '');
  }
}
